import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const initialForm = { username: "", amount: "", password: "" };
const errorText = (value) => Array.isArray(value) ? value.join(" ") : String(value || "");

export default function Checkout() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const submittingRef = useRef(false);

  const refreshProfile = useCallback(async (signal) => {
    if (!localStorage.getItem("auth_token")) return;
    try {
      const response = await axios.post("/apps-profile", {
        key_s: KEY,
        currency: localStorage.getItem("selectedCurrency"),
      }, { signal });
      if (!signal?.aborted) setProfile(response.data?.data || null);
    } catch (error) {
      if (error.code !== "ERR_CANCELED" && error.name !== "CanceledError") {
        console.error("Unable to refresh balance:", error);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refreshProfile(controller.signal);
    return () => controller.abort();
  }, [refreshProfile]);

  const balance = Number(profile?.reserve_balance);
  const hasBalance = Number.isFinite(balance) && balance >= 0;
  const displayBalance = hasBalance
    ? balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Session expired", "Please sign in before transferring funds.", "warning");
      return;
    }
    const nextErrors = {};
    const username = form.username.trim();
    const amount = Number(form.amount);
    if (!username) nextErrors.username = "Enter the recipient username.";
    if (!form.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
      nextErrors.amount = "Enter an amount greater than zero.";
    } else if (hasBalance && amount > balance) {
      nextErrors.amount = "Amount exceeds your available balance.";
    }
    if (!form.password) nextErrors.password = "Enter your account password.";
    if (!accepted) nextErrors.terms = "Please accept the terms and conditions.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setErrors({});
    try {
      const response = await axios.post("/transfer-balance", {
        username,
        amount: form.amount.trim(),
        password: form.password,
        key_s: KEY,
      });
      const result = response.data || {};
      if (result.status === "success") {
        setForm(initialForm);
        setAccepted(false);
        setShowPassword(false);
        swal("Success", result.message || "Transfer completed successfully.", "success");
        refreshProfile();
      } else if (result.status === "fail") {
        swal("Warning", result.message || "The transfer could not be completed.", "warning");
      } else if (result.validation_error) {
        setErrors(result.validation_error);
      } else {
        swal("Warning", result.message || "Please check the transfer details.", "warning");
      }
    } catch (error) {
      if (error.response?.data?.validation_error) {
        setErrors(error.response.data.validation_error);
      } else {
        swal("Transfer failed", error.response?.data?.message || "Please try again later.", "error");
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        html, body, #root { background:#00191D !important; }
        .rm-transfer, .rm-transfer * { box-sizing:border-box; }
        .rm-transfer { --navy:#00191D; --panel:#05282e; --cyan:#16B8C7; --line:#17454c; --muted:#a4bfc3; color:#fff; background:#00191D; min-height:100vh; padding:18px 4px 36px; font-size:14px; line-height:1.55; }
        .rm-transfer button,.rm-transfer input { font:inherit; }
        .rm-transfer-wrap { width:100%; max-width:1080px; margin:0 auto; }
        .rm-transfer-top { display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:16px; }
        .rm-transfer-eyebrow { color:#7edbe3; text-transform:uppercase; font-size:11px; letter-spacing:1.4px; font-weight:800; }
        .rm-transfer-heading { font-size:clamp(21px,3vw,30px); line-height:1.2; font-weight:800; margin:5px 0; color:#fff; }
        .rm-transfer-sub { color:var(--muted); margin:0; }
        .rm-transfer-chip { display:inline-flex; align-items:center; gap:7px; border:1px solid var(--line); background:#082c32; border-radius:30px; padding:8px 12px; white-space:nowrap; font-size:12px; color:#aeeef2; }
        .rm-transfer-dot { width:7px; height:7px; border-radius:50%; background:var(--cyan); }
        .rm-transfer-layout { display:grid; grid-template-columns:minmax(0,1.5fr) minmax(270px,1fr); gap:16px; align-items:start; }
        .rm-transfer-panel { background:var(--panel); border:1px solid var(--line); border-radius:20px; overflow:hidden; box-shadow:0 12px 28px rgba(0,0,0,.12); animation:rm-transfer-enter .35s ease both; }
        .rm-transfer-panel-head { padding:20px 22px; border-bottom:1px solid var(--line); display:flex; align-items:center; gap:12px; }
        .rm-transfer-icon { display:grid; place-items:center; width:42px; height:42px; flex-shrink:0; border-radius:12px; background:linear-gradient(135deg,#16B8C7,#0b7e8a); color:#00191D; font-size:18px; }
        .rm-transfer-panel-title { font-size:17px; margin:0; color:#fff; font-weight:800; }
        .rm-transfer-panel-sub { margin:2px 0 0; color:var(--muted); font-size:12px; }
        .rm-transfer-body { padding:22px; }
        .rm-transfer-field { margin-bottom:17px; }
        .rm-transfer-label { display:block; color:#e6f5f6; font-weight:700; margin-bottom:7px; }
        .rm-transfer-input-wrap { position:relative; }
        .rm-transfer-input { width:100%; height:48px; background:#001e23; border:1px solid #24535a; border-radius:12px; outline:none; color:#fff; padding:0 44px 0 14px; transition:border-color .18s,box-shadow .18s; }
        .rm-transfer-input::placeholder { color:#779da2; }
        .rm-transfer-input:focus { border-color:var(--cyan); box-shadow:0 0 0 3px rgba(22,184,199,.13); }
        .rm-transfer-input[aria-invalid='true'] { border-color:#ff8686; }
        .rm-transfer-field-icon { position:absolute; right:15px; top:50%; transform:translateY(-50%); color:#67cbd4; pointer-events:none; }
        .rm-transfer-toggle { position:absolute; right:7px; top:50%; transform:translateY(-50%); border:0; background:transparent; color:#9fe9ed; padding:9px; cursor:pointer; }
        .rm-transfer-error { display:block; color:#ff9b9b; font-size:12px; margin-top:5px; }
        .rm-transfer-terms { display:flex; gap:10px; align-items:flex-start; color:#bcd2d5; cursor:pointer; margin:6px 0 18px; }
        .rm-transfer-terms input { accent-color:var(--cyan); width:17px; height:17px; flex-shrink:0; margin-top:3px; }
        .rm-transfer-submit { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; border:0; border-radius:12px; padding:14px; min-height:50px; background:linear-gradient(115deg,#16B8C7,#42d6e0); color:#002329; font-weight:800; cursor:pointer; transition:transform .18s,filter .18s; }
        .rm-transfer-submit:hover:not(:disabled) { transform:translateY(-2px); filter:brightness(1.07); }
        .rm-transfer-submit:disabled { opacity:.65; cursor:not-allowed; }
        .rm-transfer-side { display:grid; gap:16px; }
        .rm-transfer-balance { padding:23px; background:#fff; color:#12363b; border-radius:20px; border:1px solid #e0ecee; animation:rm-transfer-enter .4s ease both; }
        .rm-transfer-balance-top { display:flex; justify-content:space-between; align-items:center; gap:10px; }
        .rm-transfer-balance-label { font-size:13px; color:#57777c; font-weight:700; }
        .rm-transfer-balance-icon { display:grid; place-items:center; width:39px; height:39px; border-radius:12px; background:#def6f7; color:#078b99; }
        .rm-transfer-balance-value { font-size:clamp(26px,4vw,35px); font-weight:850; letter-spacing:-1px; overflow-wrap:anywhere; margin:12px 0 3px; line-height:1.2; }
        .rm-transfer-balance-foot { font-size:12px; color:#64868a; margin:0; }
        .rm-transfer-note { padding:20px; }
        .rm-transfer-note h3 { font-size:15px; color:#fff; margin:0 0 12px; }
        .rm-transfer-note p { font-size:13px; color:var(--muted); margin:0 0 10px; }
        .rm-transfer-note p:last-child { margin-bottom:0; }
        .rm-transfer-note i { color:var(--cyan); margin-right:8px; }
        .rm-transfer-bottom { color:#7eaaaf; text-align:center; margin:16px 0 0; font-size:12px; }
        @keyframes rm-transfer-enter { from { opacity:.4; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @media(max-width:760px) { .rm-transfer-layout { grid-template-columns:1fr; } .rm-transfer-side { grid-row:1; } .rm-transfer-top { align-items:flex-start; } .rm-transfer-chip { margin-top:4px; } }
        @media(max-width:480px) { .rm-transfer { padding-top:12px; } .rm-transfer-top { flex-direction:column; } .rm-transfer-panel-head,.rm-transfer-body { padding:16px; } .rm-transfer-balance { padding:19px; } .rm-transfer-heading { font-size:23px; } }
        @media(prefers-reduced-motion:reduce) { .rm-transfer * { animation:none!important; transition:none!important; } }
      `}</style>

      <main className="rm-transfer">
        <div className="rm-transfer-wrap">
          <header className="rm-transfer-top">
            <div>
              <div className="rm-transfer-eyebrow">RM CROWN MARKET / Wallet</div>
              <h1 className="rm-transfer-heading">Transfer Balance</h1>
              <p className="rm-transfer-sub">Send funds to another registered account.</p>
            </div>
            <div className="rm-transfer-chip"><span className="rm-transfer-dot" /> Account transfer</div>
          </header>

          <div className="rm-transfer-layout">
            <section className="rm-transfer-panel" aria-labelledby="rm-transfer-title">
              <div className="rm-transfer-panel-head">
                <span className="rm-transfer-icon"><i className="fa-solid fa-arrow-right-arrow-left" /></span>
                <div>
                  <h2 id="rm-transfer-title" className="rm-transfer-panel-title">Transfer Information</h2>
                  <p className="rm-transfer-panel-sub">Complete the details below to submit your transfer.</p>
                </div>
              </div>
              <form className="rm-transfer-body" onSubmit={submit}>
                <div className="rm-transfer-field">
                  <label htmlFor="rm-recipient" className="rm-transfer-label">Recipient Username</label>
                  <div className="rm-transfer-input-wrap">
                    <input id="rm-recipient" name="username" type="text" className="rm-transfer-input" autoComplete="off" placeholder="Enter recipient username" value={form.username} onChange={onChange} aria-invalid={Boolean(errors.username)} required />
                    <i className="fa-solid fa-user rm-transfer-field-icon" />
                  </div>
                  {errors.username && <small className="rm-transfer-error">{errorText(errors.username)}</small>}
                </div>
                <div className="rm-transfer-field">
                  <label htmlFor="rm-amount" className="rm-transfer-label">Transfer Amount ($)</label>
                  <div className="rm-transfer-input-wrap">
                    <input id="rm-amount" name="amount" type="number" min="0.01" step="any" inputMode="decimal" className="rm-transfer-input" placeholder="Enter transfer amount" value={form.amount} onChange={onChange} aria-invalid={Boolean(errors.amount)} required />
                    <i className="fa-solid fa-dollar-sign rm-transfer-field-icon" />
                  </div>
                  {errors.amount && <small className="rm-transfer-error">{errorText(errors.amount)}</small>}
                </div>
                <div className="rm-transfer-field">
                  <label htmlFor="rm-password" className="rm-transfer-label">Account Password</label>
                  <div className="rm-transfer-input-wrap">
                    <input id="rm-password" name="password" type={showPassword ? "text" : "password"} className="rm-transfer-input" placeholder="Enter your account password" autoComplete="current-password" value={form.password} onChange={onChange} aria-invalid={Boolean(errors.password)} required />
                    <button type="button" className="rm-transfer-toggle" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}>
                      <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                  {errors.password && <small className="rm-transfer-error">{errorText(errors.password)}</small>}
                </div>
                <label className="rm-transfer-terms" htmlFor="rm-terms">
                  <input id="rm-terms" type="checkbox" checked={accepted} onChange={(e) => { setAccepted(e.target.checked); setErrors((old) => ({ ...old, terms: "" })); }} />
                  <span>I agree to the terms and conditions and have verified the recipient and amount.</span>
                </label>
                {errors.terms && <small className="rm-transfer-error" style={{ marginTop: -12, marginBottom: 12 }}>{errorText(errors.terms)}</small>}
                <button className="rm-transfer-submit" type="submit" disabled={submitting}>
                  <i className="fa-solid fa-paper-plane" /> {submitting ? "Submitting transfer..." : "Send Balance"}
                </button>
              </form>
            </section>

            <aside className="rm-transfer-side">
              <div className="rm-transfer-balance">
                <div className="rm-transfer-balance-top">
                  <span className="rm-transfer-balance-label">Available Balance</span>
                  <span className="rm-transfer-balance-icon"><i className="fa-solid fa-wallet" /></span>
                </div>
                <div className="rm-transfer-balance-value">${displayBalance}</div>
                <p className="rm-transfer-balance-foot">Reserve wallet balance</p>
              </div>
              <div className="rm-transfer-panel rm-transfer-note">
                <h3><i className="fa-solid fa-shield-halved" /> Before you transfer</h3>
                <p><i className="fa-solid fa-circle-check" /> Double-check the recipient username.</p>
                <p><i className="fa-solid fa-circle-check" /> Confirm the amount before submitting.</p>
                <p><i className="fa-solid fa-circle-info" /> Completed transfers may not be reversible.</p>
              </div>
            </aside>
          </div>
          <p className="rm-transfer-bottom">Your transfer is processed using the existing account transfer service.</p>
        </div>
      </main>
    </>
  );
}
